<template>
  <article class="pi-opp" :aria-labelledby="ids.title">
    <div class="pi-opp__head">
      <h3 :id="ids.title" ref="title" class="pi-opp__title" tabindex="-1">
        <pi-company-name :company-id="item.company.companyId" :shard-code="item.company.shardCode" :name="item.company.name" :query="companyQuery"/>
        <span class="pi-opp__sep" aria-hidden="true">·</span>
        <router-link v-if="item.feature.route" :to="item.feature.route" class="pi-link">{{ item.feature.name }}</router-link>
        <span v-else>{{ item.feature.name }}</span>
      </h3>
      <div class="pi-opp__badges">
        <span class="pi-badge" :class="'pi-badge--' + item.statusTone">Status: {{ item.statusText }}</span>
        <span class="pi-badge pi-badge--muted">{{ item.strengthText }}</span>
        <span v-if="item.noResponse" class="pi-badge pi-badge--warn" :title="noResponseText">Sem retorno</span>
      </div>
    </div>
    <p v-if="factsText" class="pi-opp__facts">{{ factsText }}</p>

    <dl class="pi-opp__grid">
      <div class="pi-opp__fact">
        <dt>Estado comercial</dt>
        <dd>{{ item.commercialStateText }}</dd>
      </div>
      <div v-if="item.detectedText" class="pi-opp__fact">
        <dt>Detecção</dt>
        <dd>{{ item.detectedText }}<span v-if="item.expiresText" class="pi-table__sub">{{ item.expiresText }}</span></dd>
      </div>
    </dl>

    <div class="pi-opp__section">
      <h4 class="pi-opp__label">Observação</h4>
      <p class="pi-opp__text">{{ item.why || 'Sem descrição da regra.' }}</p>
    </div>

    <div class="pi-opp__section">
      <h4 class="pi-opp__label">Evidência</h4>
      <ul v-if="item.evidence.length" class="pi-opp__list">
        <li v-for="evidence in item.evidence" :key="evidence.key" class="pi-opp__evidence">
          <strong>{{ evidence.ruleText }}</strong>
          <span v-for="signal in evidence.signals" :key="signal.key" class="pi-opp__signal">{{ signal.label }}: {{ signal.value }}</span>
          <span v-if="evidence.metaText" class="pi-table__sub">{{ evidence.metaText }}</span>
        </li>
      </ul>
      <p v-else class="pi-muted">Sem evidência registrada.</p>
    </div>

    <div class="pi-opp__section">
      <h4 class="pi-opp__label">Limitações</h4>
      <ul class="pi-opp__list pi-opp__limits">
        <li v-for="(limitation, index) in item.limitations" :key="index">{{ limitation }}</li>
      </ul>
    </div>

    <div v-if="item.nextStep" class="pi-opp__section">
      <h4 class="pi-opp__label">Próximo passo sugerido</h4>
      <p class="pi-opp__text">{{ item.nextStep }}</p>
    </div>

    <p v-if="item.convertedHintText" class="pi-opp__hint">{{ item.convertedHintText }}</p>
    <p v-if="item.previousText" class="pi-muted pi-opp__previous">{{ item.previousText }}</p>

    <div class="pi-opp__status">
      <p class="pi-opp__status-text">
        <span class="pi-opp__strong">Status: {{ item.statusText }}</span>
        <template v-if="item.reasonText"> · motivo: {{ item.reasonText }}</template>
        <template v-if="item.statusMetaText"> · {{ item.statusMetaText }}</template>
      </p>
      <p v-if="item.noResponse" class="pi-opp__text">{{ noResponseText }}.</p>
      <p class="pi-sr" role="status" aria-live="polite">{{ announcement }}</p>

      <div v-if="item.actions.length && choosing === null" ref="actions" class="pi-opp__actions" role="group"
           :aria-label="'Mudar o status de ' + item.title">
        <button v-for="action in item.actions" :key="action.value" type="button" class="pi-btn pi-btn--ghost"
                :aria-label="action.label + ': ' + item.title" :disabled="busy" @click="choose(action.value)">
          {{ action.label }}
        </button>
      </div>
      <p v-else-if="!item.actions.length" class="pi-muted">Status final: não há mudança de status disponível.</p>

      <form v-if="choosing !== null" class="pi-opp__form" @submit.prevent="confirm">
        <label class="pi-opp__field" :for="ids.reason">
          <span>Motivo para "{{ choosingLabel }}"</span>
          <select :id="ids.reason" ref="reason" v-model="reason" class="form-control pi-input" required
                  :aria-describedby="error ? ids.error : null">
            <option value="" disabled>Escolha o motivo</option>
            <option v-for="option in reasons" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
        </label>
        <button type="submit" class="pi-btn" :disabled="busy || !reason"
                :aria-label="'Confirmar a mudança para ' + choosingLabel + ': ' + item.title">{{ busy ? 'Salvando…' : 'Confirmar' }}</button>
        <button type="button" class="pi-btn pi-btn--ghost" :disabled="busy" :aria-label="'Cancelar a mudança de status: ' + item.title"
                @click="dismiss">Cancelar</button>
      </form>

      <div v-if="error" :id="ids.error" class="pi-opp__error" role="alert">
        <span>{{ error }}</span>
        <button v-if="conflict" type="button" class="pi-btn pi-btn--ghost" aria-label="Recarregar a lista de oportunidades"
                @click="$emit('reload')">Recarregar a lista</button>
      </div>
    </div>

    <div class="pi-opp__history">
      <button type="button" class="pi-opp__toggle" :aria-expanded="historyOpen ? 'true' : 'false'" :aria-controls="ids.history"
              :aria-label="historyText + ' de ' + item.title" @click="toggleHistory">{{ historyText }}</button>
      <div v-show="historyOpen" :id="ids.history" class="pi-opp__history-body">
        <p v-if="history.loading" class="pi-muted" role="status">Carregando o histórico…</p>
        <div v-else-if="history.error" class="pi-opp__error" role="alert">
          <span>{{ historyErrorText }}</span>
          <button v-if="history.error.retryable" type="button" class="pi-btn pi-btn--ghost" :aria-label="'Tentar de novo carregar o histórico de ' + item.title"
                  @click="loadHistory(true)">Tentar de novo</button>
        </div>
        <ol v-else-if="events.length" class="pi-opp__events">
          <li v-for="event in events" :key="event.key">
            {{ event.atText }} · {{ event.byText }} · {{ event.changeText }}<template v-if="event.reasonText"> · motivo: {{ event.reasonText }}</template>
          </li>
        </ol>
        <p v-else-if="history.loaded" class="pi-muted">Sem mudanças registradas.</p>
      </div>
    </div>
  </article>
</template>

<script>
import piCompanyName from "./piCompanyName.vue";
import { changeOpportunityStatus, piGet } from "./piApi.js";
import { blankRequest, listPayloadOf, objectOf, runRequest, shouldLoad } from "./piRequests.js";
import {
  NO_RESPONSE_TEXT, actionAllows, eventRows, isConflict, revenueError, statusChangeError, statusLabel,
} from "./piOpportunitiesModel.js";
import { PI_REVENUE_OPPORTUNITY_EVENTS } from "../../../../urlConfig";

let sequence = 0;

export default {
  name: "pi-opportunity-card",
  components: { piCompanyName },
  props: {
    item: { type: Object, required: true },
    catalog: { type: Object, default: null },
  },
  data() {
    sequence += 1;
    const prefix = "pi-opp-" + sequence;
    return {
      ids: {
        title: prefix + "-title",
        reason: prefix + "-reason",
        error: prefix + "-error",
        history: prefix + "-history",
      },
      noResponseText: NO_RESPONSE_TEXT,
      companyQuery: {},
      choosing: null,
      reason: "",
      busy: false,
      error: "",
      conflict: false,
      announcement: "",
      historyOpen: false,
      history: blankRequest(),
    };
  },
  computed: {
    factsText() {
      return [this.item.company.factsText, this.item.feature.classText].filter(Boolean).join(" · ");
    },
    chosenAction() {
      return this.item.actions.filter((action) => action.value === this.choosing)[0] || null;
    },
    reasons() {
      return this.chosenAction ? this.chosenAction.reasons : [];
    },
    choosingLabel() {
      return this.chosenAction ? this.chosenAction.statusLabel : statusLabel(this.choosing, this.catalog);
    },
    events() {
      return eventRows(listPayloadOf(this.history.envelope), this.catalog);
    },
    historyText() {
      return this.historyOpen ? "Esconder o histórico" : "Ver o histórico";
    },
    historyErrorText() {
      const error = revenueError(this.history.error);
      return error ? error.message : "";
    },
  },
  methods: {
    choose(status) {
      this.choosing = status;
      this.reason = "";
      this.error = "";
      this.conflict = false;
      this.announcement = "";
      this.$nextTick(() => {
        const select = this.$refs.reason;
        if (select && typeof select.focus === "function") select.focus();
      });
    },
    cancel() {
      this.choosing = null;
      this.reason = "";
      this.error = "";
      this.conflict = false;
    },
    dismiss() {
      this.cancel();
      this.restoreFocus();
    },
    restoreFocus() {
      this.$nextTick(() => {
        const group = this.$refs.actions;
        const button = group && typeof group.querySelector === "function" ? group.querySelector("button:not([disabled])") : null;
        const target = button || this.$refs.title;
        if (target && typeof target.focus === "function") target.focus();
      });
    },
    confirm() {
      if (this.busy || this.choosing === null) return;
      if (!actionAllows(this.item.actions, this.choosing, this.reason)) {
        this.error = "Escolha um dos motivos da lista para esta mudança.";
        return;
      }
      const id = this.item.id;
      const label = this.choosingLabel;
      this.busy = true;
      this.error = "";
      this.conflict = false;
      changeOpportunityStatus(id, this.choosing, this.reason)
        .then((envelope) => {
          this.busy = false;
          const updated = objectOf(envelope && envelope.data);
          this.cancel();
          this.announcement = "Status alterado para " + label + ".";
          if (updated.id === undefined || updated.id === null || updated.id === "") {
            this.restoreFocus();
            this.$emit("reload");
            return;
          }
          this.$emit("updated", updated);
          this.restoreFocus();
          if (this.historyOpen) this.loadHistory(true);
        })
        .catch((error) => {
          this.busy = false;
          this.error = statusChangeError(error);
          this.conflict = isConflict(error);
        });
    },
    toggleHistory() {
      this.historyOpen = !this.historyOpen;
      if (this.historyOpen) this.loadHistory(false);
    },
    loadHistory(force) {
      const id = this.item.id;
      if (!id || !shouldLoad(this.history, id, force)) return;
      runRequest(this.history, () => piGet(PI_REVENUE_OPPORTUNITY_EVENTS(id)), "carregar o histórico", id);
    },
  },
};
</script>

<style src="./pi.css"></style>

<style scoped>
.pi-opp {
  padding: 14px 16px;
  border: 1px solid #e6e7eb;
  border-radius: 6px;
  background: #ffffff;
}

.pi-opp__head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px 12px;
}

.pi-opp__title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #1f2933;
}

.pi-opp__title .pi-link {
  font-size: 14px;
}

.pi-opp__sep {
  margin: 0 6px;
  color: #6b7280;
}

.pi-opp__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.pi-opp__facts {
  margin: 4px 0 10px;
  font-size: 12px;
  color: #4e5560;
}

.pi-opp__grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 28px;
  margin: 0 0 10px;
}

.pi-opp__fact dt {
  font-size: 11px;
  font-weight: 700;
  color: #4e5560;
  text-transform: uppercase;
}

.pi-opp__fact dd {
  margin: 0;
  font-size: 12px;
  color: #1f2933;
}

.pi-opp__section {
  margin-bottom: 8px;
}

.pi-opp__label {
  margin: 0 0 2px;
  font-size: 12px;
  font-weight: 700;
  color: #3a424e;
}

.pi-opp__text {
  margin: 0;
  font-size: 12px;
  color: #3a424e;
}

.pi-opp__list {
  margin: 0;
  padding-left: 18px;
  font-size: 12px;
  color: #3a424e;
}

.pi-opp__evidence {
  margin-bottom: 4px;
}

.pi-opp__signal {
  display: inline-block;
  margin-left: 8px;
}

.pi-opp__limits {
  color: #4e5560;
}

.pi-opp__hint {
  margin: 0 0 8px;
  padding: 6px 10px;
  border-radius: 4px;
  background: #e7f6ec;
  font-size: 12px;
  color: #0f6b2b;
}

.pi-opp__previous {
  margin-bottom: 8px;
}

.pi-opp__status {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #eef0f2;
}

.pi-opp__status-text {
  margin: 0 0 6px;
  font-size: 12px;
  color: #3a424e;
}

.pi-opp__strong {
  font-weight: 700;
}

.pi-opp__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.pi-opp__form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px;
  margin-top: 6px;
}

.pi-opp__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  color: #4e5560;
}

.pi-opp__field .form-control {
  width: 220px;
}

.pi-opp__error {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  font-size: 12px;
  color: #a3172b;
}

.pi-opp__history {
  margin-top: 10px;
}

.pi-opp__toggle {
  padding: 0;
  border: 0;
  background: none;
  color: #1b59f8;
  font-size: 12px;
  cursor: pointer;
}

.pi-opp__toggle:hover,
.pi-opp__toggle:focus {
  color: #1340b5;
  text-decoration: underline;
}

.pi-opp__history-body {
  margin-top: 6px;
}

.pi-opp__events {
  margin: 0;
  padding-left: 18px;
  font-size: 12px;
  color: #3a424e;
}

@media (max-width: 767px) {
  .pi-opp__field .form-control {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/components/pi/piOpportunityCard.vue